import * as React from 'react';
import { useForkRef } from '../../../hooks';

import { Styler } from '../../Styler/Styler';
import type { StylerProps } from '../../Styler/Styler';
import { usePopoverContext } from '../Popover.context';

export type PopoverAnchorProps = {
    /**
     * The trigger element.
     */
    children: React.ReactNode;

    /**
     * If `true`, the child element will be cloned.
     */
    asChild?: boolean;

    /**
     * Additional properties applied to the root Styler component.
     */
    stylerProps?: StylerProps;
};

export const PopoverAnchor = React.forwardRef<HTMLElement, PopoverAnchorProps>(function PopoverAnchor(
    props,
    ref
): React.JSX.Element {
    const { children, asChild = false, stylerProps, ...other } = props;

    const { refs, getReferenceProps } = usePopoverContext();

    // Merge floating-ui reference setter with external ref
    const handleRef = useForkRef(refs.setReference as React.Ref<Element>, ref);

    if (asChild && React.isValidElement(children)) {
        const childProps = children.props as React.HTMLAttributes<HTMLElement>;

        return React.cloneElement(
            children,
            getReferenceProps({
                ...childProps,
                ...other,
                ...stylerProps,
                ref: handleRef, // MUST be applied last (React 19 stricter ref handling)
            })
        );
    }

    return (
        <Styler ref={handleRef} {...getReferenceProps(other)} {...stylerProps}>
            {children}
        </Styler>
    );
});
